import { useMutation } from '@tanstack/react-query';
import { aiScribeAPI } from 'api/panels';
import clsx from 'clsx';
import LoadingSpinner from 'components/loader/LoadingSpinner';
import { AiScribeFormVariable, AiScribeTypes } from 'components/module/cards/AiScribeCard';
import SituationBuilder from 'components/resources/triggers/SituationBuilder';
import Button from 'components/utils/Button';
import Checkbox from 'components/utils/Checkbox';
import { ComboBox } from 'components/utils/ComboBox';
import Input from 'components/utils/Input';
import Label from 'components/utils/Label';
import { ModuleContext } from 'components/utils/module/ModuleContext';
import { AvoSlideCloseModal } from 'components/utils/panels/AvoSlideCloseModal';
import { SlidePanel } from 'components/utils/panels/AvoSlidePanel';
import { RadioButton } from 'components/utils/RadioButton';
import RequiredMark from 'components/utils/requiredMark';
import { Body2, Caption2, H4 } from 'components/utils/typo';
import VariableAutoGenerator from 'components/utils/variable-generator';
import { useTriggers } from 'hooks/module/resources/useTriggers';
import { X } from 'lucide-react';
import { useContext, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { getVariableName } from 'utils/gptUtils';

interface AiScribeFormProps {
  aiScribeId: number;
  onSave: () => void;
  onCancel: () => void;
}

export const AiScribeForm = ({ aiScribeId, onSave, onCancel }: AiScribeFormProps) => {
  const { module } = useContext(ModuleContext);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const { data: triggers } = useTriggers();
  const triggerOptions = triggers.map((trigger) => ({
    value: trigger.id,
    label: trigger.title,
    isEditingDisabled: trigger.candidate_trigger || trigger.is_global,
  }));
  const [triggerFormOpen, setTriggerFormOpen] = useState(false);
  const [triggerId, setTriggerId] = useState<number | undefined>();
  const triggerComponentProps = {
    type: module?.type,
    toggleModal: () => setTriggerFormOpen(false),
    selectTrigger: (value) => setValue('trigger', value, { shouldDirty: true }),
    createModal: true,
    triggerId,
    savePosition: () => {},
  };

  const {
    handleSubmit,
    setValue,
    watch,
    register,
    reset,
    formState: { isDirty, errors },
  } = useFormContext<AiScribeFormVariable>();

  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync } = useMutation({
    mutationFn: aiScribeId
      ? (data: AiScribeFormVariable) => aiScribeAPI.patch(aiScribeId, data)
      : aiScribeAPI.post,
  });

  const onSubmit = async (data: AiScribeFormVariable) => {
    if (isLoading) return;
    setIsLoading(true);
    if (!data.variable) {
      data.variable = await getVariableName(data.title);
    }
    isDirty && (await mutateAsync(data));
    reset(data);
    onSave();
    setIsLoading(false);
  };
  return (
    <>
      <form className='flex h-full flex-col' onSubmit={handleSubmit(onSubmit)}>
        <div className='grow overflow-y-auto'>
          <div className='sticky top-0 z-10 flex items-center justify-between bg-white pl-4 pr-3 pt-[20px]'>
            <H4>Text Input</H4>
            <div
              className='cursor-pointer'
              onClick={() => (isDirty ? setCloseModalOpen(true) : onCancel())}
            >
              <X size={20} />
            </div>
          </div>
          <div className='mt-4 px-4'>
            <div className='space-y-1'>
              <div className='flex gap-1'>
                <Body2 className='text-gray-900'>Trigger</Body2>
                <RequiredMark />
              </div>
              <ComboBox
                onChange={(value: any) => setValue('trigger', value, { shouldDirty: true })}
                options={triggerOptions}
                selectedValue={watch('trigger')}
                onCreate={() => {
                  setTriggerId(undefined);
                  setTriggerFormOpen(true);
                }}
                onEdit={(id) => {
                  setTriggerId(+id);
                  setTriggerFormOpen(true);
                }}
                createButtonLabel='Create New Trigger'
              />
            </div>
            <div className='mt-6 space-y-1'>
              <div className='flex gap-1'>
                <Body2 className='text-gray-900'>Title</Body2>
                <RequiredMark />
              </div>
              <Input
                {...register('title', {
                  required: { value: true, message: 'Title field is required' },
                  maxLength: 200,
                })}
                maxLength={200}
                className={clsx(
                  errors.title &&
                    'border-red-500 ring-1 !ring-red-500' /* TODO: Need to remove ring after remove global style*/
                )}
              />
              {errors.title && <span className='text-error'>{errors.title.message}</span>}
              <Caption2 className='flex justify-end text-gray-text-weak'>
                {watch('title').length}/200
              </Caption2>
            </div>
            <div className='mt-1 space-y-1'>
              <div className='flex gap-1'>
                <Body2 className='text-gray-900'>Subtitle</Body2>
              </div>
              <Input {...register('subtitle', { maxLength: 200 })} maxLength={200} />
              <Caption2 className='flex justify-end text-gray-text-weak'>
                {watch('subtitle').length}/200
              </Caption2>
            </div>
            <div className='!mb-6 !mt-3'>
              <div className='space-y-[5px]'>
                <div className='flex gap-1'>
                  <Body2 className='text-gray-900'>Note Type</Body2>
                  <RequiredMark />
                </div>
                <div className='space-y-1'>
                  {AiScribeTypes.map((item) => (
                    <Label
                      key={item.value}
                      className={clsx(
                        'flex gap-2 rounded bg-gray-100 px-2 py-3',
                        item.disabled && '!cursor-default'
                      )}
                    >
                      <RadioButton {...register('type')} {...item} />
                      <span className={clsx(item.disabled ? 'text-gray-300' : 'text-gray-900')}>
                        {item.label}
                      </span>
                    </Label>
                  ))}
                </div>
              </div>
            </div>
            <div className='mt-6'>
              <Label className='inline-flex items-center gap-1'>
                <Checkbox
                  checked={watch('is_optional')}
                  onChange={(e) => setValue('is_optional', e.target.checked, { shouldDirty: true })}
                />
                <Body2>Make this optional</Body2>
              </Label>
            </div>
            <div className='my-6'>
              <VariableAutoGenerator
                question={watch('title')}
                tagTitle='Message'
                variable={watch('variable')}
                setVariableName={(variable) =>
                  setValue('variable', variable, { shouldDirty: true })
                }
              />
            </div>
          </div>
        </div>
        <div className='shrink-0'>
          <div className='flex justify-end gap-2 px-[12px] pb-[22px] pt-[12px]'>
            <div className='flex'>
              <Button.Reverse
                type='button'
                onClick={() => (isDirty ? setCloseModalOpen(true) : onCancel())}
                disabled={isLoading}
              >
                Cancel
              </Button.Reverse>
            </div>
            <div className='flex w-[80px]'>
              <Button disabled={isLoading}>{isLoading ? <LoadingSpinner /> : 'Save'}</Button>
            </div>
          </div>
        </div>
      </form>
      <AvoSlideCloseModal
        open={closeModalOpen}
        onContinue={() => setCloseModalOpen(false)}
        onDiscard={onCancel}
      />
      <SlidePanel open={triggerFormOpen} onClose={() => setTriggerFormOpen(false)}>
        <SituationBuilder {...triggerComponentProps} />
      </SlidePanel>
    </>
  );
};
